/**
 * PortalTaxLiability — the client-facing Tax Liability page (Slice 2).
 *
 * Two views inside one route:
 *   • Summary  — "My Tax Liabilities" with current balances, upcoming
 *                due dates, and a list of published reports.
 *   • Report   — the same clean report layout the firm previews, but
 *                read-only. Print/PDF works.
 *
 * Only PUBLISHED reports are fetchable from the portal API — drafts stay
 * on the firm side.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Printer, FileText, AlertTriangle, CheckCircle2, Info } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const TYPES_LABEL = { gst: "GST", income_tax: "Income Tax", paye: "PAYE", other_ird: "Other IRD Debt" };
const nzMoney = (v) => v == null || v === 0 ? "NZ$0.00" : `NZ$${Number(v).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const nzDate = (iso) => !iso ? "—" : (() => { try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso.slice(0, 10); } })();

function daysOutTone(d) {
    if (d === null || d === undefined) return "bg-zinc-100 text-zinc-700 border-zinc-200";
    if (d < 0) return "bg-red-100 text-red-800 border-red-200";
    if (d <= 14) return "bg-amber-100 text-amber-900 border-amber-200";
    return "bg-sky-50 text-sky-800 border-sky-200";
}
function daysOutLabel(d) {
    if (d === null || d === undefined) return "No date";
    if (d < 0) return `${Math.abs(d)}d overdue`;
    if (d === 0) return "Due today";
    if (d === 1) return "Due tomorrow";
    return `${d}d left`;
}

export default function PortalTaxLiability() {
    return <SummaryView />;
}

export function PortalTaxLiabilityReport() {
    const { reportId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/portal/tax-liability/reports/${reportId}`);
                if (alive) setData(data);
            } catch (e) { toast.error(formatApiError(e)); nav("/portal/tax-liability", { replace: true }); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [reportId, nav]);

    if (loading || !data) return <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading report…</div>;
    const { client, snapshot, report, lines, total_liability } = data;
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap print:hidden">
                <Link to="/portal/tax-liability" className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]" data-testid="ptl-back">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to Tax Liabilities
                </Link>
                <Button variant="outline" onClick={() => window.print()} data-testid="ptl-print"><Printer className="h-4 w-4 mr-1" /> Print / PDF</Button>
            </div>
            <Card className="p-8 max-w-3xl mx-auto shadow-sm" data-testid="ptl-report">
                <div className="border-b border-[#EEEDE7] pb-5 mb-5">
                    <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#5E636E]">Tax Liability Report</div>
                    <h1 className="mt-1 text-2xl font-semibold text-[#0F1115]">{client.name}</h1>
                    <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-[#5E636E]">
                        <div><b className="text-[#0F1115]">IRD number:</b> {client.ird_number || "—"}</div>
                        <div><b className="text-[#0F1115]">Report date:</b> {nzDate(report.report_date)}</div>
                        <div><b className="text-[#0F1115]">Version:</b> v{report.version_number}</div>
                        <div><b className="text-[#0F1115]">Prepared by:</b> {report.prepared_by_name || "—"}</div>
                        <div><b className="text-[#0F1115]">Snapshot:</b> v{snapshot.version_number} · {nzDate(snapshot.captured_at)}</div>
                        <div><b className="text-[#0F1115]">Published:</b> {nzDate(report.portal_published_at)}</div>
                    </div>
                </div>
                <section>
                    <h2 className="text-[14px] font-semibold text-[#0F1115] mb-2">Tax liability summary</h2>
                    <table className="w-full text-[13px]">
                        <thead className="text-[11px] uppercase tracking-wider text-[#5E636E]">
                            <tr><th className="text-left py-1.5">Liability</th><th className="text-left py-1.5">Due date</th><th className="text-right py-1.5">Amount</th></tr>
                        </thead>
                        <tbody>
                            {lines.map((l) => (
                                <tr key={l.type} className="border-t border-[#EEEDE7]">
                                    <td className="py-2">{l.label}{l.description ? <div className="text-[11px] text-[#5E636E]">{l.description}</div> : null}</td>
                                    <td className="py-2 text-[#5E636E]">{nzDate(l.due_date)}</td>
                                    <td className="py-2 text-right tabular-nums">{nzMoney(l.amount)}</td>
                                </tr>
                            ))}
                            <tr className="border-t-2 border-[#0F1115]">
                                <td className="py-2 font-semibold" colSpan={2}>Total outstanding liability</td>
                                <td className="py-2 text-right tabular-nums font-bold text-[15px]">{nzMoney(total_liability)}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
                {report.comments && <section className="mt-5"><h2 className="text-[14px] font-semibold text-[#0F1115] mb-1">Comments</h2><p className="text-[13px] whitespace-pre-wrap">{report.comments}</p></section>}
                {report.recommendations && <section className="mt-5"><h2 className="text-[14px] font-semibold text-[#0F1115] mb-1">Recommendations</h2><p className="text-[13px] whitespace-pre-wrap">{report.recommendations}</p></section>}
                <div className="mt-6 pt-4 border-t border-[#EEEDE7] text-[11px] text-[#5E636E]">
                    This report reflects the information available at the time it was prepared. Balances may change if IRD processes future assessments, transfers or adjustments.
                </div>
            </Card>
        </div>
    );
}

function SummaryView() {
    const [data, setData] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: s }, { data: rs }] = await Promise.all([
                api.get("/portal/tax-liability/summary"),
                api.get("/portal/tax-liability/reports"),
            ]);
            setData(s); setReports(rs || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>;

    if (!data?.has_snapshot) {
        return (
            <div className="space-y-4" data-testid="ptl-empty">
                <header>
                    <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">My Tax Liabilities</h1>
                    <p className="text-sm text-[#5E636E] mt-0.5">A snapshot of every tax balance we're tracking for you.</p>
                </header>
                <Card className="p-10 text-center">
                    <Info className="h-8 w-8 text-sky-600 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-[#0F1115]">No liability snapshot yet</div>
                    <div className="text-[12px] text-[#5E636E] mt-1">Your accountant hasn't captured your myIR balances into ClientOps yet. Once they do, your current amounts and any published reports will show here.</div>
                </Card>
            </div>
        );
    }

    const c = data.current;
    return (
        <div className="space-y-4">
            <header>
                <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">My Tax Liabilities</h1>
                <p className="text-sm text-[#5E636E] mt-0.5">Latest snapshot v{c.snapshot_version} captured {nzDate(c.snapshot_date)}. IRD number {c.ird_number || "—"}.</p>
            </header>

            {/* Total banner */}
            <Card className="p-5 bg-gradient-to-br from-[#0F1115] to-[#2E2E2E] text-white border-0" data-testid="ptl-total-card">
                <div className="text-[11px] uppercase tracking-[0.14em] opacity-70">Total outstanding liability</div>
                <div className="mt-1 text-4xl font-semibold tabular-nums">{nzMoney(c.total)}</div>
                {data.upcoming.length > 0 && (
                    <div className="mt-2 text-[12px] opacity-80">Next due: <b>{data.upcoming[0].label}</b> · {nzDate(data.upcoming[0].due_date)} · {daysOutLabel(data.upcoming[0].days_out)}</div>
                )}
            </Card>

            {/* Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <BreakdownTile label="GST" amount={c.gst} due={c.gst_due_date} />
                <BreakdownTile label="Income Tax" amount={c.income_tax} due={c.income_tax_due_date} />
                <BreakdownTile label="PAYE" amount={c.paye} due={c.paye_due_date} />
                <BreakdownTile label="Other IRD Debt" amount={c.other_ird?.amount} due={c.other_ird?.due_date} sub={c.other_ird?.description} />
            </div>

            {/* Upcoming due dates */}
            {data.upcoming.length > 0 && (
                <Card className="p-0 overflow-hidden">
                    <div className="border-b border-[#EEEDE7] bg-[#FAFAF7] px-4 py-2 text-[11.5px] uppercase tracking-wider text-[#5E636E]">Upcoming due dates</div>
                    <ul className="divide-y divide-[#EEEDE7]">
                        {data.upcoming.map((u, i) => (
                            <li key={i} className="px-4 py-2.5 flex items-center gap-3 text-[13px]" data-testid={`ptl-due-${i}`}>
                                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${daysOutTone(u.days_out)}`}>{daysOutLabel(u.days_out)}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-[#0F1115] truncate">{u.label}</div>
                                    <div className="text-[11.5px] text-[#5E636E]">{nzDate(u.due_date)}</div>
                                </div>
                                <div className="text-[14px] font-semibold tabular-nums">{nzMoney(u.amount)}</div>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {/* Published reports */}
            <Card className="p-0 overflow-hidden">
                <div className="border-b border-[#EEEDE7] bg-[#FAFAF7] px-4 py-2 flex items-center gap-2">
                    <div className="text-[11.5px] uppercase tracking-wider text-[#5E636E]">Published reports</div>
                    <span className="text-[11px] text-[#5E636E]">({reports.length})</span>
                </div>
                {reports.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#5E636E]" data-testid="ptl-reports-empty">
                        Your accountant hasn't published a report for you yet.
                    </div>
                ) : (
                    <ul className="divide-y divide-[#EEEDE7]" data-testid="ptl-reports">
                        {reports.map((r) => (
                            <li key={r.id} className="px-4 py-3 flex items-center gap-3 text-[13px]" data-testid={`ptl-report-${r.id}`}>
                                <FileText className="h-4 w-4 text-[#5E636E] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-[#0F1115]">Tax Liability Report v{r.version_number}</div>
                                    <div className="text-[11.5px] text-[#5E636E]">Published {nzDate(r.portal_published_at)} · Prepared by {r.prepared_by_name || "—"} · Covers {r.selected_types?.map((t) => TYPES_LABEL[t] || t).join(", ")}</div>
                                </div>
                                <Link to={`/portal/tax-liability/reports/${r.id}`} data-testid={`ptl-open-${r.id}`}>
                                    <Button size="sm" variant="outline" className="h-8 text-[12px]">View report</Button>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            <div className="text-[11px] text-[#5E636E] flex items-start gap-1.5 max-w-2xl">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Balances shown here reflect the last snapshot your accountant captured from myIR. IRD may process future assessments, transfers or adjustments that change these figures.</span>
            </div>
        </div>
    );
}

function BreakdownTile({ label, amount, due, sub }) {
    const hasAmount = amount && amount > 0;
    return (
        <div className={`rounded-xl border p-3 ${hasAmount ? "border-[#E4E4E7] bg-white" : "border-emerald-200 bg-emerald-50/40"}`} data-testid={`ptl-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-[#5E636E]">{label}</span>
                {!hasAmount && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
            </div>
            <div className={`mt-1 text-xl font-semibold tabular-nums ${hasAmount ? "text-[#0F1115]" : "text-emerald-800"}`}>{nzMoney(amount)}</div>
            {sub && <div className="text-[10.5px] text-[#5E636E] mt-0.5 truncate">{sub}</div>}
            {hasAmount && due && <div className="text-[10.5px] text-[#5E636E] mt-0.5">Due {nzDate(due)}</div>}
        </div>
    );
}
